import { Box, Paper, Typography } from "@mui/material";
import {
	CellKeyDownEvent,
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	IStatusPanelParams,
	MenuItemDef,
	RowClickedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
	Group,
	Panel,
	Separator,
	useDefaultLayout,
} from "react-resizable-panels";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ErrorModal } from "../../../../components/modals/error-modal";
import { CenteredLazyLoading } from "../../../../components/shared/CenteredLazyLoading";
import { ExcelButton } from "../../../../components/shared/buttons/ExcelButton";
import { CustomStatusPanel } from "../../../../components/shared/status-bar/CustomStatusPanel";
import { useLogbookSettings } from "../../../../context/LogbookSettingsContext";
import { useUserContext } from "../../../../context/userContext";
import { useContextMenuMESF, useShiftNavigator } from "../../../../controls";
import { getMomentTz } from "../../../../services";
import { useGridDefinitions } from "../../useGridDefinitions";
import { routeLogbookEntry, useEntries } from "../hooks/useEntries";
import { useLogbookEntryOptionsFunctions } from "../hooks/useLogbookEntryOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { ENTRY_INITIAL_VALUES, Entry } from "../models/Entry";
import { RemoveEntryDialog } from "./dialog/DeleteEntryDialog";
import { EntryViewer } from "./entry-viewer/EntryViewer";
import { Filters } from "./filters/filters";

const moment = getMomentTz();

// Default layout matching original Grid md:5 / md:7 ratio (41.67% / 58.33%)
const DEFAULT_LAYOUT = { left: 42, right: 58 };

export const Logbook = () => {
	const { entry } = useLogbookSettings();
	const {
		state: { defaultAreaId },
	} = useUserContext();

	const withAssetFilter = entry.withAssetFilter;
	const filterAssets = entry.filterAssets;
	const canEditAsset = entry.canEditAsset;
	const presetAssetId = entry.presetAssetId;
	const allowAttachments = entry.allowAttachments;
	const canEditShift = entry.canEditShift;
	const showShiftCrew = entry.showShiftCrew;
	const exportToExcel = entry.exportToExcel;
	const shiftsRange = entry.shiftsRange;
	const entryActions = entry.entryActions;
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const entryId = searchParams.get("entryId");
	const action = searchParams.get("action");
	const { shiftId } = useParams();

	const { shiftInfo, getShiftDataFromAPI } = useShiftNavigator();

	const isEditMode = action === "edit";

	const shiftIdRef = useRef<number | null>(null);
	const gridApiRef = useRef<GridApi | null>(null);

	const [error, setError] = useState("");
	const [show, setShow] = useState<"edit" | "view" | "">("");

	const [quickFilter, setQuickFilter] = useState("");
	const [assetId, setAssetId] = useState<number | null>(
		presetAssetId ?? (typeof defaultAreaId === "number" ? defaultAreaId : null),
	);
	const initializedAssetRef = useRef(false);

	const [selectedRowData, setSelectedRowData] = useState<Entry | null>(null);
	const [showRemoveDialog, setShowRemoveDialog] = useState(false);

	const { registerConfig, showContextMenu } = useContextMenuMESF();

	// Resizable panels layout persistence
	const { defaultLayout: savedLayout, onLayoutChange } = useDefaultLayout({
		id: "logbook-entry-v2",
		storage: localStorage,
	});
	// Use saved layout or default to avoid flash on initial render
	const resolvedLayout = savedLayout ?? DEFAULT_LAYOUT;

	const {
		data: rows,
		isLoading,
		error: e,
		isError,
		refetch,
	} = useEntries({
		shiftId: shiftInfo?.CurrentShiftId,
		assetId: withAssetFilter ? assetId : undefined,
	});

	const { columnDefs } = useTableData({
		showContextMenu,
		allowAttachments,
		withAssetFilter,
		showShiftCrew,
	});
	const { defaultColDef } = useGridDefinitions({});

	const onQuickFilterChange = (text: string) => {
		setQuickFilter(text);
		gridApiRef.current?.setGridOption("quickFilterText", text);
	};

	const handleNewEntry = () => {
		gridApiRef.current?.deselectAll();
		setSelectedRowData({
			...ENTRY_INITIAL_VALUES,
		});
		navigate(`${routeLogbookEntry}/${shiftIdRef.current}?action=new`, {
			replace: true,
		});
		setShow("edit");
	};

	const rowClicked = (event: RowClickedEvent) => {
		const currentShiftId = shiftId || shiftInfo?.CurrentShiftId;
		setSelectedRowData(event?.data);
		navigate(
			`${routeLogbookEntry}/${currentShiftId}?entryId=${event?.data?.EntryId}&action=view`,
		);
		setShow("view");
	};

	const onCellKeyDown = useCallback(
		(event: CellKeyDownEvent) => {
			const { api, node, event: keyEvent } = event;
			if (!keyEvent) return;
			const key = (keyEvent as KeyboardEvent).key;

			if (key === "ArrowUp" || key === "ArrowDown") {
				keyEvent.preventDefault();
				const currentIndex = node.rowIndex !== null ? node.rowIndex : -1;
				const nextRowIndex =
					key === "ArrowUp" ? currentIndex - 1 : currentIndex + 1;
				const nextNode = api.getDisplayedRowAtIndex(nextRowIndex);

				if (nextNode) {
					const currentShiftId = shiftId || shiftInfo?.CurrentShiftId;
					nextNode.setSelected(true);
					api.ensureIndexVisible(nextRowIndex);
					setSelectedRowData(nextNode.data);
					navigate(
						`${routeLogbookEntry}/${currentShiftId}/?entryId=${nextNode.data.EntryId}&action=view`,
					);
				}
			}
		},
		[setSelectedRowData, shiftId, shiftInfo?.CurrentShiftId],
	);

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			showContextMenu(event as any, data, "logbookEntryContext");
			if (data) {
				params.api.deselectAll();
				params.node?.setSelected(true);
			}
			return [];
		},
		[rows],
	);

	const { getMenuOptions } = useLogbookEntryOptionsFunctions({
		setSelectedRowData,
		setShow,
		setShowRemoveDialog,
		handleNewEntry,
	});

	const handleExportToExcel = () => {
		if (!gridApiRef.current) return;

		const params = {
			fileName: "Logbook_Entries_Export.xlsx",
			columnKeys: [
				"AssetName",
				"CreateTimestamp",
				"Shift",
				"Crew",
				"Title",
				"CreatedBy",
				"Author",
				"Content",
			],
			processCellCallback: (params: any) => {
				if (params.column.colId === "CreateTimestamp") {
					return moment(params.value).format("MM/DD/yy - HH:mm");
				}
				return params.value;
			},
		};
		gridApiRef.current.exportDataAsExcel(params);
	};

	useEffect(() => {
		if (!isLoading && entryId && entryId !== "new" && gridApiRef.current) {
			const rowNode = gridApiRef.current?.getRowNode(entryId);

			if (rowNode) {
				gridApiRef.current?.ensureNodeVisible(rowNode);

				rowNode.setSelected(true);
				setSelectedRowData(rowNode.data);
				setShow(isEditMode ? "edit" : "view");
			} else {
				// If the row node is not found, it might be a newly created entry
				// Try to refetch the data and then look for the entry
				setTimeout(async () => {
					await refetch();
					const newRowNode = gridApiRef.current?.getRowNode(entryId);
					if (newRowNode) {
						gridApiRef.current?.ensureNodeVisible(newRowNode);
						newRowNode.setSelected(true);
						setSelectedRowData(newRowNode.data);
						setShow(isEditMode ? "edit" : "view");
					}
				}, 500);
			}
		}
	}, [gridApiRef.current, isLoading, entryId, isEditMode, refetch]);

	useEffect(() => {
		registerConfig({
			id: "logbookEntryContext",
			getOptions: getMenuOptions,
		});
	}, []);

	useEffect(() => {
		if (isError) {
			setError(e.message);
		}
	}, [isError, e]);

	// Sync assetId with defaultAreaId when it becomes available (only once on initial load)
	useEffect(() => {
		if (
			!initializedAssetRef.current &&
			!presetAssetId &&
			typeof defaultAreaId === "number"
		) {
			setAssetId(defaultAreaId);
			initializedAssetRef.current = true;
		}
	}, [defaultAreaId, presetAssetId]);

	useEffect(() => {
		if (shiftInfo) {
			setShow("");
			setSelectedRowData(null);
			if (
				entryId &&
				(shiftIdRef.current == null ||
					shiftIdRef.current === shiftInfo?.CurrentShiftId)
			) {
				navigate(
					`${routeLogbookEntry}/${shiftInfo.CurrentShiftId}?entryId=${entryId}&action=${action}`,
				);
			} else {
				navigate(`${routeLogbookEntry}/${shiftInfo.CurrentShiftId}`);
			}
			shiftIdRef.current = shiftInfo?.CurrentShiftId;
			(async () => {
				await refetch();
			})();
		}
	}, [shiftInfo]);

	useEffect(() => {
		if (!entry?.useShiftNavigatorBasedOnAsset) {
			if (shiftId) {
				(async () => {
					const shiftIdNumber = Number(shiftId);
					await getShiftDataFromAPI(null, shiftIdNumber);
				})();
			} else {
				(async () => {
					await getShiftDataFromAPI(null, 0);
				})();
			}
		}
	}, []);

	useEffect(() => {
		if (!entry?.useShiftNavigatorBasedOnAsset) return;

		const assetForShift =
			assetId ?? (defaultAreaId != null ? Number(defaultAreaId) : null);

		if (assetForShift == null || Number.isNaN(assetForShift)) return;

		(async () => {
			if (shiftId) {
				const currentDate = shiftInfo?.CurrentProductionDate ?? null;
				await getShiftDataFromAPI(currentDate, 0, assetForShift);
			} else {
				await getShiftDataFromAPI(new Date(), 0, assetForShift);
			}
		})();
	}, [assetId, defaultAreaId]);

	return (
		<Box
			sx={{
				width: "100%",
				height: "calc(100vh - 130px)",
				display: "flex",
				flexDirection: "column",
			}}
		>
			<Box sx={{ mb: 2 }}>
				<Typography
					variant="h5"
					style={{
						fontWeight: 600,
						userSelect: "none",
					}}
				>
					LOGBOOK
				</Typography>
			</Box>
			<Group
				id="logbook-entry-group"
				orientation="horizontal"
				defaultLayout={resolvedLayout}
				onLayoutChange={onLayoutChange}
				style={{ flex: 1, width: "100%" }}
			>
				<Panel
					id="left"
					defaultSize={42}
					minSize={25}
					style={{ paddingBottom: 5 }}
				>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: 1,
							height: "100%",
							pr: 1,
						}}
					>
						<Filters
							withAssetFilter={withAssetFilter}
							filterAssets={filterAssets}
							quickFilter={quickFilter}
							onQuickFilterChange={onQuickFilterChange}
							assetId={assetId}
							setAssetId={setAssetId}
						/>
						<Paper
							elevation={1}
							style={{
								flex: 1,
								width: "100%",
							}}
						>
							<AgGridReact<Entry>
								loading={isLoading}
								rowData={rows}
								columnDefs={columnDefs}
								defaultColDef={defaultColDef}
								rowHeight={38}
								headerHeight={42}
								loadingOverlayComponent={CenteredLazyLoading}
								animateRows
								getContextMenuItems={getContextMenuItems}
								onRowClicked={rowClicked}
								onCellKeyDown={onCellKeyDown}
								onGridReady={(params) => (gridApiRef.current = params.api)}
								getRowId={(params) => `${params.data.EntryId}`}
								gridOptions={{
									rowSelection: {
										mode: "singleRow",
										checkboxes: false,
										enableClickSelection: true,
									},
									statusBar: {
										statusPanels: [
											exportToExcel
												? {
														statusPanel: (params: IStatusPanelParams) => (
															<div
																style={{
																	display: "flex",
																	alignItems: "center",
																	paddingTop: 5,
																}}
															>
																<ExcelButton
																	handleExportToExcel={handleExportToExcel}
																/>
															</div>
														),
														align: "right",
													}
												: {},
											{
												statusPanel: (params: IStatusPanelParams) => (
													<CustomStatusPanel handleNew={handleNewEntry} />
												),
												align: "left",
											},
										],
									},
								}}
							/>
						</Paper>
					</Box>
				</Panel>
				<Separator
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "col-resize",
					}}
				>
					<Box
						sx={{
							width: 8,
							height: 48,
							borderRadius: 1,
							backgroundColor: "#e0e0e0",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							gap: "3px",
							transition: "background-color 0.2s",
							"&:hover": {
								backgroundColor: "primary.main",
								"& .grip-dot": {
									backgroundColor: "#fff",
								},
							},
						}}
					>
						{[...Array(5)].map((_, i) => (
							<Box
								key={i}
								className="grip-dot"
								sx={{
									width: 4,
									height: 4,
									borderRadius: "50%",
									backgroundColor: "#9e9e9e",
									transition: "background-color 0.2s",
								}}
							/>
						))}
					</Box>
				</Separator>
				<Panel
					id="right"
					defaultSize={58}
					minSize={30}
					style={{ paddingBottom: 5 }}
				>
					<Box sx={{ pl: 1, height: "100%" }}>
						<EntryViewer
							entry={selectedRowData}
							show={show}
							onChangeShow={() => {
								const newShow = show === "view" ? "edit" : "view";
								setShow(newShow);
								if (entryId && entryId !== "new") {
									const targetUrl =
										newShow === "edit"
											? `/logbook/entry/${
													shiftId || shiftInfo?.CurrentShiftId
												}?entryId=${entryId}&action=edit`
											: `/logbook/entry/${
													shiftId || shiftInfo?.CurrentShiftId
												}?entryId=${entryId}&action=view`;
									navigate(targetUrl);
								}
							}}
							preselectedAssetId={assetId}
							withAssetFilter={withAssetFilter}
							canEditAsset={canEditAsset}
							canEditShift={canEditShift}
							filterAssets={filterAssets}
							allowAttachments={allowAttachments}
							shiftsRange={shiftsRange}
							entryActions={entryActions}
							onHide={async (
								entryId?: number | null,
								shouldStayInEditMode?: boolean,
							) => {
								if (entryId) {
									// First refetch to ensure we have the latest data
									await refetch();

									const targetUrl = shouldStayInEditMode
										? `/logbook/entry/${
												shiftId || shiftInfo?.CurrentShiftId
											}?entryId=${entryId}&action=edit`
										: `/logbook/entry/${
												shiftId || shiftInfo?.CurrentShiftId
											}?entryId=${entryId}&action=view`;
									navigate(targetUrl);
									setShow(shouldStayInEditMode ? "edit" : "view");

									// Additional delay to allow navigation and state updates to complete
									setTimeout(() => {
										const rowNode = gridApiRef.current?.getRowNode(
											entryId.toString(),
										);
										if (rowNode) {
											gridApiRef.current?.ensureNodeVisible(rowNode);
											rowNode.setSelected(true);
											setSelectedRowData(rowNode.data);
										} else {
											// If still not found, try one more refetch
											setTimeout(async () => {
												await refetch();
												const retryRowNode = gridApiRef.current?.getRowNode(
													entryId.toString(),
												);
												if (retryRowNode) {
													gridApiRef.current?.ensureNodeVisible(retryRowNode);
													retryRowNode.setSelected(true);
													setSelectedRowData(retryRowNode.data);
												} else {
													setShow("");
													setSelectedRowData(null);
													gridApiRef.current?.deselectAll();
												}
											}, 500);
										}
									}, 200);
								} else {
									setShow("");
									setSelectedRowData(null);
									gridApiRef.current?.deselectAll();
									navigate(
										`${routeLogbookEntry}/${shiftId || shiftInfo?.CurrentShiftId}`,
									);
								}
							}}
						/>
					</Box>
				</Panel>
			</Group>
			<RemoveEntryDialog
				entry={selectedRowData}
				show={showRemoveDialog}
				onHide={(shouldUpdate) => {
					if (shouldUpdate) {
						refetch();
					}
					setShowRemoveDialog(false);
					setSelectedRowData(null);
					setShow("");
				}}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Box>
	);
};
